import * as Sentry from '@sentry/react';

import { ENV } from '@/shared/config/env';
import { logger } from '@/shared/lib/logger';

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Similar to Flutter implementation in main.dart:67-75
 */
export function initSentry() {
  // Don't initialize Sentry in development
  if (ENV.NODE_ENV === 'development') {
    logger.general.debug('Sentry disabled in development mode');
    return;
  }

  // Don't initialize if no DSN provided (like in Flutter with empty ctDsn)
  if (!ENV.SENTRY_DSN) {
    logger.general.warn('Sentry DSN not provided, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: ENV.SENTRY_DSN,
    environment: ENV.APP_STAGE, // 'dev', 'preprod', 'prod' like Flutter
    debug: ENV.NODE_ENV === 'development', // Like kDebugMode in Flutter

    // Performance monitoring - 20% like in Flutter (o.tracesSampleRate = 0.20)
    tracesSampleRate: 0.2,

    // Additional React-specific configuration
    integrations: [
      Sentry.browserTracingIntegration({
        // Set up automatic route change tracking for SPA
        enableInp: true,
      }),
      Sentry.replayIntegration({
        // Capture replays on errors (useful for debugging)
        maskAllText: ENV.NODE_ENV === 'production',
        blockAllMedia: ENV.NODE_ENV === 'production',
      }),
    ],

    // Session replay sample rate
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of error sessions

    // Release tracking
    release: ENV.APP_VERSION,

    // Filter out development noise
    beforeSend: (event) => {
      // In development, we might want to filter some events
      if (ENV.NODE_ENV === 'development') {
        // You can add custom filtering logic here
        logger.general.debug('Sentry event captured', { event });
      }
      return event;
    },

    // Set user context
    initialScope: {
      tags: {
        component: 'react-frontend',
        app: ENV.APP_NAME,
      },
    },
  });

  logger.general.info('Sentry initialized', { environment: ENV.APP_STAGE });
}

/**
 * Helper functions for manual error tracking
 */
export const sentryHelpers = {
  captureError: (error: Error, context?: Record<string, unknown>) => {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value as Record<string, unknown>);
        });
      }
      Sentry.captureException(error);
    });
  },

  captureMessage: (message: string, level: 'info' | 'warning' | 'error' = 'info') => {
    Sentry.captureMessage(message, level);
  },

  setUser: (user: { id?: string; email?: string; username?: string }) => {
    Sentry.setUser(user);
  },

  addBreadcrumb: (message: string, category?: string, data?: Record<string, unknown>) => {
    Sentry.addBreadcrumb({
      message,
      category: category || 'custom',
      data,
      level: 'info',
    });
  },
};
